import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { Spacing, Input, Button, FlatButton } from '@reservamos/elements';
import SectionTitle from 'ui/atoms/SectionTitle';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import ExchangeInfoModal from 'ui/molecules/ExchangeInfoModal';
import { EXCHANGE_TYPEAHEAD_CLEAR } from 'constants/ActionTypes';
import ExchangeTypeahead from '../../ExchangeTypeahead';
import GfaDisclaimer from './GfaDisclaimer';

/**
 * The form's field stack: a plain column under the redesign, where each field's own top margin
 * is the spacing, and the tenant's usual `Spacing` row everywhere else.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.isStacked - Whether the redesign's single column is in use.
 * @param {React.ReactNode} props.children - The fields.
 * @returns {JSX.Element}
 */
const FieldStack = ({ isStacked, children, ...spacingProps }) =>
  isStacked ? (
    <div className="flex w-full flex-col">{children}</div>
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Spacing {...spacingProps}>{children}</Spacing>
  );

FieldStack.propTypes = {
  isStacked: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const propTypes = {
  handleSubmit: PropTypes.func,
  title: PropTypes.string,
  ctaText: PropTypes.string,
  handleRedirectExchangeAlternative: PropTypes.func,
  redirectText: PropTypes.string,
  showRedirectButton: PropTypes.bool,
  handleClickButton: PropTypes.func,
  hideTitle: PropTypes.bool,
};
/**
 * Form to exchange a ticket on GFA
 * @param {Object} props - Object component props
 * @param {Function} props.handleSubmit - Function to be executed when submitting the form
 * @param {Object} props.title - Title to show in the exchange section
 * @param {Object} props.ctaText - Cta text of the submit
 * @param {Function} props.handleRedirectExchangeAlternative - Function to execute to redirect to another type of exchange
 * @param {String} props.redirectText - Text to show in the redirect button
 * @param {Boolean} props.showRedirectButton - Flag to show or hide the redirect button
 * @param {Function} props.handleClickButton - Function to execute when clicking the submit button
 * @param {Boolean} [props.hideTitle] - Whether to leave the title out, when the line selector above
 *   the form already titles the step
 * @returns Component
 */
const GfaForm = ({
  handleSubmit,
  title,
  ctaText,
  handleRedirectExchangeAlternative,
  redirectText,
  showRedirectButton,
  handleClickButton,
  hideTitle = false,
}) => {
  const { env, features } = useSelector((state) => state.whitelabelConfig);
  const useNewExchangeUi = Boolean(features.USE_NEW_EXCHANGE_UI);
  // The typeahead already asks the design system for `XL`. Under the redesign the plain field
  // beside it asks for the same, so the column is one set of fields rather than two shapes.
  const fieldRadius = useNewExchangeUi ? 'XL' : undefined;
  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [originError, setOriginError] = useState(false);
  const [destinationError, setDestinationError] = useState(false);
  const [originUrl, setOriginUrl] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const placesBaseUrl = `${env.api.searchUrl}/v2/places`;

  const { originSlug, originDisplay, destinationDisplay, destinationSlug } = useSelector(
    (state) => state.exchange.trip,
  );
  const dispatch = useDispatch();

  const { t } = useTranslation('exchange');

  const initialValues = {
    operationNumber: '',
  };
  const validationSchema = yup.object().shape({
    operationNumber: yup
      .string()
      .min(6, t('validation.min_digits', { digits: 6 }))
      .max(20, t('validation.max_digits', { digits: 20 }))
      // eslint-disable-next-line no-useless-escape
      .matches(/([A-Z]+)\-(\d+)/, t('validation.operation_number_invalid'))
      .required(t('validation.operation_number_required')),
  });

  useEffect(() => {
    if (originInput !== '' && !originSlug) {
      setOriginUrl(`${placesBaseUrl}?q=${originInput}`);
    }
  }, [originInput, placesBaseUrl, originDisplay, originSlug]);

  useEffect(() => {
    if (destinationInput !== '' && destinationDisplay === '') {
      setDestinationUrl(`${placesBaseUrl}?q=${destinationInput}&from=${originSlug}`);
    }
  }, [destinationDisplay, destinationInput, originSlug, placesBaseUrl]);

  const isDestinationDisabled = !originSlug;

  useEffect(() => {
    if (originDisplay !== '') {
      setOriginInput(originDisplay);
      setOriginError(false);
    }
  }, [originDisplay]);

  useEffect(() => {
    if (destinationDisplay !== '') {
      setDestinationInput(destinationDisplay);
      setDestinationError(false);
    }
  }, [destinationDisplay]);

  const clearTypeaheadData = (origin = false, destination = false) => {
    dispatch({
      type: EXCHANGE_TYPEAHEAD_CLEAR,
      origin,
      destination,
    });
  };

  const handleKeyUp = (control, event) => {
    event.persist();

    if (event.keyCode === 8 || event.key === 'Backspace') {
      if (control === 'origin' && originSlug !== '') {
        clearTypeaheadData(true, true);
        setDestinationInput('');
      }

      if (control === 'destination' && destinationSlug !== '') {
        clearTypeaheadData(false, true);
      }
    }
  };

  const originValidation = () => {
    if (!originSlug) {
      setOriginError(true);
      setOriginUrl(`${placesBaseUrl}?q=${originInput}`);
      return false;
    }
    return true;
  };

  const destinationValidation = () => {
    if (!destinationSlug) {
      setDestinationError(true);
      setDestinationUrl(`${placesBaseUrl}?q=${destinationInput}&from=${originSlug}`);
      return false;
    }
    return true;
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (originValidation() && destinationValidation()) {
            handleSubmit({
              ...values,
              operationNumbers: [values.operationNumber],
              originSlug,
              destinationSlug,
            });
          }
        }}
      >
        {({ values, handleChange, handleBlur, errors, touched }) => (
          <Form>
            <Spacing vertical size={useNewExchangeUi ? 'M' : 'L'}>
              {!hideTitle && (
                <SectionTitle title={title} description={t('subtitle.input_ticket_details')} />
              )}
              {/* The redesign stacks the fields: the shell gives the form a narrow column, and
                  three fields sharing a row left each one too small to read its own label.

                  The stack adds no spacing of its own there. Each field already carries 20px
                  above it — the room its floating label needs — which is the gap the buttons
                  below sit on, so anything added on top only set the fields further apart. */}
              <FieldStack isStacked={useNewExchangeUi} flexGrow isResponsive responsiveSize="S">
                <ExchangeTypeahead
                  label={t('labels.origin')}
                  name="origin"
                  onChange={setOriginInput}
                  url={originUrl}
                  value={originInput}
                  onKeyUp={handleKeyUp}
                  errorMessage={t('validation.origin_slug_required')}
                  hasError={originError}
                  handleBlur={originValidation}
                />
                <ExchangeTypeahead
                  label={t('labels.destination')}
                  name="destination"
                  onChange={setDestinationInput}
                  url={destinationUrl}
                  value={destinationInput}
                  isDisabled={isDestinationDisabled}
                  onKeyUp={handleKeyUp}
                  errorMessage={t('validation.destination_slug_required')}
                  hasError={destinationError}
                  handleBlur={destinationValidation}
                />
                <Input
                  label={t('labels.input_operation_number', { context: 'gfa' })}
                  id="operationNumber"
                  errorMessage={errors.operationNumber}
                  hasError={errors.operationNumber !== undefined && touched.operationNumber}
                  maxLength={20}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.operationNumber}
                  borderRadius={fieldRadius}
                />
              </FieldStack>
              {/* Where to find the numbers is answered by the artwork the shell shows beside the
                  form, so the modal would be a second answer to the same question. Kept for every
                  tenant that has not opted into the redesign. */}
              {!useNewExchangeUi && <ExchangeInfoModal />}
              {/* The redesign stacks the actions under the fields, each the full width of the
                  column and spaced like the fields above — `XL` is the 20px the field grid uses.
                  The link to the other form comes first, the submit last. */}
              {useNewExchangeUi ? (
                <Spacing vertical fullWidth size="M">
                  {showRedirectButton && (
                    <FlatButton onClick={handleRedirectExchangeAlternative}>
                      {redirectText}
                    </FlatButton>
                  )}
                  <Button
                    buttonType="submit"
                    text={ctaText}
                    variant="accent"
                    withHeight
                    fullWidth
                    isRounded
                    onClick={handleClickButton}
                  />
                </Spacing>
              ) : (
                <Spacing justifyContent="space-between" isResponsive>
                  {/* On the redesigned shell this block is rendered below the form instead, in
                      the footer that every brand shares. */}
                  <GfaDisclaimer />
                  <Spacing isResponsive responsiveColumnReverse>
                    {showRedirectButton && (
                      <FlatButton onClick={handleRedirectExchangeAlternative}>
                        {redirectText}
                      </FlatButton>
                    )}
                    <Button
                      buttonType="submit"
                      text={ctaText}
                      variant="accent"
                      withHeight
                      fullWidthOnSmall
                      onClick={handleClickButton}
                    />
                  </Spacing>
                </Spacing>
              )}
            </Spacing>
          </Form>
        )}
      </Formik>
    </>
  );
};

GfaForm.propTypes = propTypes;

export default GfaForm;
